import { TerminalsModel } from "../../src/models/terminals-model";
import { FleetAlarm } from "../../src/models/types/voy-fleet-alarm-model.types";
import { VesselsModel } from "../../src/models/vessels-model";
import { AuthService } from "../../src/services/auth-service";
import {
  AlarmAcceptanceSource,
  AlarmState,
  AlarmType,
} from "../../src/services/types/fleet-alarms-service.types";
import { NotificationType } from "../../src/services/types/fleet-notification-service.types";
import { AuditLog, AuditLogState, AuditLogStatus, WeatherRoutingNotification } from "../../src/services/types/fleet-weather-routing-notification.types";
import { GetFormattedDateNoYear, getUTCDateTimeString } from "../../src/utils/helpers";
import { SvgIcons } from "../voyage-svg-icons/src/voyage-svg-icons";

export const getWeatherAlarmIcon = (alarmType: AlarmType) => {
  if (!alarmType) return SvgIcons.heavyWeatherAlarm;
  switch (alarmType) {
    case AlarmType.HeavyWeather:
      return SvgIcons.heavyWeatherAlarm;
    case AlarmType.ParametricRoll:
      return SvgIcons.parametricRollAlarm;
    default:
      return SvgIcons.heavyWeatherAlarm;
  }
};

export const getNotificationMessage = (alarm: FleetAlarm) => {
  // Emma Maersk (IMO: 9321483) with the risk of heavy weather | 16 May 13:02 UTC.
  if (!alarm) return "";
  const vesselName = VesselsModel!.getVesselName(alarm?.imo);
  const alarmType =
    alarm?.alarmType === AlarmType.HeavyWeather
      ? "Heavy weather"
      : "Parametric roll";

  if (alarm.alarmState === AlarmState.Active) {
    const alarmTime = getFormattedDateTime(alarm.startTs);
    return `${vesselName} (IMO: ${alarm?.imo}) with risk of ${alarmType} on  ${alarmTime}.`;
  }

  const updatedLog = alarm.alarmLogs?.find(
    (log) =>
      log.fromState === AlarmState.Active &&
      log.toState === AlarmState.InProgress
  );
  const alarmUpdatedAt = formatTimeDifference(updatedLog?.createdAt ?? "");
  const user = updatedLog?.userData?.displayName;
  const userId = updatedLog?.userData?.email;
  const currentUserId = AuthService.getCurrentUserId();
  if(!userId){
    return `${alarmType} alert for ${vesselName} (IMO: ${alarm?.imo}) accepted by Vessel ${alarmUpdatedAt}.`;
  }
  else if(userId === currentUserId){
    return `${alarmType} alert for ${vesselName} (IMO: ${alarm?.imo}) accepted by me ${alarmUpdatedAt}.`;
  } else {
    return `${alarmType} alert for ${vesselName} (IMO: ${alarm?.imo}) accepted by ${user} ${alarmUpdatedAt}.`;
  }
};

export const getBPRNotificationMessage = (alarm: NotificationType) => {
  if (!alarm) return "";
  const vesselName = VesselsModel!.getVesselName(alarm?.imo_no);
 
  return `${vesselName} (IMO: ${alarm?.imo_no}) has dismissed Star Route`;
};

export const getOfflineNotificationMessage = (notification: NotificationType) => {
    const formattedDate = GetFormattedDateNoYear(notification.created_at);
    const vesselName = VesselsModel!.getVesselName(notification.imo_no);

    return `Cloud Vessel Offline | ${vesselName} IMO(${notification.imo_no}) | ${formattedDate}`;
  }

export const getFormattedDate = (timestamp: string): string => {
  if (!timestamp) return "";
  // 15 May 13:02 UTC
  const date = new Date(timestamp);
  const day = date.getDate();
  //utc
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const month = date.toLocaleString("default", { month: "short" });
  return `${day} ${month} ${hours}:${minutes} UTC`;
};

export const formatTimeDifference = (timestamp: string): string => {
  if (!timestamp) return "";
  const startTime = new Date(timestamp).getTime();
  const currentTime = Date.now();
  const timeDiff = currentTime - startTime;

  const seconds = Math.floor(timeDiff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 30) {
    return "just now";
  } else if (seconds < 60) {
    return `${seconds} seconds ago`;
  } else if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  } else if (days < 7) {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  } else if (weeks < 4) {
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  } else if (months < 12) {
    return `${months} month${months === 1 ? "" : "s"} ago`;
  } else {
    return `${years} year${years === 1 ? "" : "s"} ago`;
  }
};

export const filterAlarms = (alarms: FleetAlarm[], searchQuery: string, needAction: boolean) => {

    if(needAction) {
        // filter alarms based alarm state active
        alarms = alarms.filter((alarm) => alarm.alarmState === AlarmState.Active);
    }
  // filter alarms based on last updated time
  alarms.sort((a, b) => {
    const aTime = new Date(a?.alarmLogs?.[0]?.createdAt).getTime();
    const bTime = new Date(b?.alarmLogs?.[0]?.createdAt).getTime();
    return bTime - aTime;
  });

  return alarms.filter((alarm) => {
    const alarmVesselName = VesselsModel!.getVesselName(alarm.imo);
    return (
      (alarmVesselName
        ? alarmVesselName.toLowerCase().includes(searchQuery)
        : false) || alarm.imo.toString().includes(searchQuery)
    );
  });
};

export const filterNotifications = (notifications: NotificationType[], searchQuery: string, needAction: boolean) => {
  if(needAction) {
    notifications = notifications.filter((notification) => notification.state.toLocaleLowerCase() === AlarmState.Active);
  }
  
  notifications.sort((a, b) => {
    const aTime = new Date(a?.created_at).getTime();
    const bTime = new Date(b?.created_at).getTime();
    return bTime - aTime;
  });

  return notifications.filter((notification) => {
    const alarmVesselName = VesselsModel!.getVesselName(notification.imo_no);
    return (
      (alarmVesselName
        ? alarmVesselName.toLowerCase().includes(searchQuery)
        : false) || notification.imo_no.toString().includes(searchQuery)
    );
  });
}

export const getFormattedDateTime = (timestamp: string): string => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const day = date.getUTCDate();
  const hours = date.getUTCHours().toString().padStart(2, '0');;
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');;
  const month = date.toLocaleString("default", { month: "short" });
  return `${day} ${month} at ${hours}:${minutes} UTC`;
};

export const getTooltipContent = (alarm: FleetAlarm) => {
  const vesselName = VesselsModel!.getVesselName(alarm?.imo);
  const alarmAcknowledgedLog = alarm.alarmLogs?.find(
    (log) =>
      log.fromState === AlarmState.Active &&
      log.toState === AlarmState.InProgress &&
      log.reasonSource?.toLocaleLowerCase() === AlarmAcceptanceSource.Vessel
  );
  const alarmAcknowledgedAt = getFormattedDate(alarmAcknowledgedLog?.createdAt ?? "");

  const alarmAcceptedLog = alarm.alarmLogs?.find(
    (log) =>
      log.fromState === AlarmState.Active &&
      log.toState === AlarmState.InProgress &&
      log.reasonSource?.toLocaleLowerCase() !== AlarmAcceptanceSource.Vessel &&
      (log.userData?.displayName || alarm.userData?.email)

  );
  const alarmAcceptedAt = getFormattedDate(alarmAcceptedLog?.createdAt ?? "");

  const alarmClosedLog = alarm.alarmLogs?.find(
    (log) =>
      log.toState === AlarmState.Ended
  );
  const alarmClosedAt = getFormattedDate(alarmClosedLog?.createdAt ?? "");

  let user = alarmAcceptedLog?.userData?.displayName;
  const userId = alarmAcceptedLog?.userData?.email;
  const currentUserId = AuthService.getCurrentUserId();
  user = userId === currentUserId ? "me" : user;

  let content = '';
  content += `<span>Raised on vessel ${getFormattedDate(alarm.created_at)}.<br></span>`;

  if(alarmAcknowledgedAt)
    content += `<span>Acknowledged by vessel ${alarmAcknowledgedAt}.<br></span>`;
  if(alarmAcceptedAt)
    content += `<span>Accepted by ${user} ${alarmAcceptedAt}.<br></span>`;
  if(alarmClosedAt)
    content += `<span>Closed ${alarmClosedAt}.<br></span>`;

  return content;
}

export const getAlarmLogContent = (alarm: FleetAlarm) => {
  const vesselName = VesselsModel!.getVesselName(alarm?.imo);
  const alarmRaisedLog = alarm.alarmLogs?.find(
    (log) =>
      log.toState === AlarmState.Active &&
      log.reasonSource?.toLocaleLowerCase() === AlarmAcceptanceSource.Vessel
  );
  const alarmRaisedAt = getFormattedDateTime(alarmRaisedLog?.createdAt ?? "");
  const alarmAcknowledgedLog = alarm.alarmLogs?.find(
    (log) =>
      log.fromState === AlarmState.Active &&
      log.toState === AlarmState.InProgress &&
      log.reasonSource?.toLocaleLowerCase() === AlarmAcceptanceSource.Vessel
  );
  const alarmAcknowledgedAt = getFormattedDateTime(alarmAcknowledgedLog?.createdAt ?? "");

  const alarmAcceptedLog = alarm.alarmLogs?.find(
    (log) =>
      log.fromState === AlarmState.Active &&
      log.toState === AlarmState.InProgress &&
      log.reasonSource?.toLocaleLowerCase() !== AlarmAcceptanceSource.Vessel &&
      (log.userData?.displayName || alarm.userData?.email)

  );
  const alarmAcceptedAt = getFormattedDateTime(alarmAcceptedLog?.createdAt ?? "");

  const alarmClosedLog = alarm.alarmLogs?.find(
    (log) =>
      log.toState === AlarmState.Ended
  );
  const alarmClosedAt = getFormattedDateTime(alarmClosedLog?.createdAt ?? "");

  let user = alarmAcceptedLog?.userData?.displayName;
  const userId = alarmAcceptedLog?.userData?.email;
  const currentUserId = AuthService.getCurrentUserId();
  user = userId === currentUserId ? "me" : user;

  let content = '';
  content += `<span>Raised on vessel on ${alarmRaisedAt}.<br></span>`;

  if(alarmAcknowledgedAt)
    content += `<span>Acknowledged by vessel on ${alarmAcknowledgedAt}.<br></span>`;
  if(alarmAcceptedAt)
    content += `<span>Accepted by ${user} on ${alarmAcceptedAt}.<br></span>`;
  if(alarmClosedAt)
    content += `<span>Closed on ${alarmClosedAt}.<br></span>`;

  var span = document.createElement('span');
  span.innerHTML = content;
  return span;
}

export const filterWeatherRoutingAuditLogs = (auditLogs: AuditLog[], searchQuery: string, needAction: boolean) => {

  if(needAction) {
    auditLogs = auditLogs.filter((auditLog) => auditLog.status.toLowerCase() === AuditLogState.UNREAD);
  }
  
  auditLogs.sort((a, b) => {
    const aTime = new Date(a?.created_at).getTime();
    const bTime = new Date(b?.created_at).getTime();
    return bTime - aTime;
  });

  return auditLogs.filter((auditLog) => {
    const alarmVesselName = VesselsModel!.getVesselName(auditLog.imo);
    return (
      (alarmVesselName
        ? alarmVesselName.toLowerCase().includes(searchQuery)
        : false) || auditLog.imo.toString().includes(searchQuery)
    );
  });
}

export const getAuditLogMessage = (auditLog: AuditLog, isForNotes: boolean = false): string => {
  if (!auditLog) return "";
  const vesselName = VesselsModel!.getVesselName(auditLog?.imo);
  const fromTerminal = TerminalsModel?.getTerminal(auditLog.terminal_from || '')?.city;
  const toTerminal = TerminalsModel?.getTerminal(auditLog.terminal_to || '')?.city;
  const date = getUTCDateTimeString(auditLog.created_at);

  const boldWrap = (text: string) => isForNotes ? text : `<b><i>${text}</i></b>`;

  switch (auditLog.mst_status_name) {
    case AuditLogStatus.PlANSUBMITTEDTOWNI:
      return `${vesselName} (IMO: ${auditLog.imo}) has ${boldWrap("requested weather routing")} from ${fromTerminal} to ${toTerminal} on ${date}.`;

    case AuditLogStatus.WEBHOOKRECEIVEDFROMWNI:
      return `${vesselName} (IMO: ${auditLog.imo}) has ${boldWrap("received weather routing from WNI")} for ${fromTerminal} to ${toTerminal} on ${date}.`;

    case AuditLogStatus.WRADVICESAFETYCHECKED: {
      const metadata = JSON.parse(auditLog.metadata || '{}');
      const safetyCheckPassed = metadata?.safetyChecksInfo?.isPassed;

      if (safetyCheckPassed) {
        return `${vesselName} (IMO: ${auditLog.imo}) has received weather routing from WNI for ${fromTerminal} to ${toTerminal} on ${date} for which ${boldWrap("safety checks are passed")}.`;
      } else {
        let reason = '';
        if (metadata?.safetyChecksInfo?.performance != null) reason = "Performance";
        else if (metadata?.safetyChecksInfo?.heavyWeather != null) reason = "Heavy Weather";
        else if (metadata?.safetyChecksInfo?.parametricRoll != null) reason = "Parametric Roll";

        const failedReason = reason ? ` due to ${reason}.` : '.';
        return `${vesselName} (IMO: ${auditLog.imo}) has received weather routing from WNI for ${fromTerminal} to ${toTerminal} on ${date} for which ${boldWrap("safety checks are not passed" + failedReason)}`;
      }
    }

    case AuditLogStatus.WRADVICEREJECTED:
      return `${vesselName} (IMO: ${auditLog.imo}) has ${boldWrap("rejected weather routing advice")} from WNI and sent a counter-proposal for ${fromTerminal} to ${toTerminal} on ${date}.`;

    case AuditLogStatus.WRADVICEACCEPTED:
      return `${vesselName} (IMO: ${auditLog.imo}) has ${boldWrap("accepted the weather routing advice")} for ${fromTerminal} to ${toTerminal} on ${date}.`;

    default:
      return "";
  }
};
